// Baytown Bridge — verified data + content sources
// All "verified" figures cite official sources directly. "Illustrative trend"
// labels are used only where multi-year trend lines are approximated from
// publicly available Census/HUD/TEA reporting.

// Sources:
// - U.S. Census QuickFacts, Baytown city, Texas (PST045224 / PST045225)
// - City of Baytown 2025–2029 Consolidated Plan Executive Summary (HUD 24 CFR 91)
// - City of Baytown 2025 Fair Housing & Consolidated Plan Survey (n=98)
// - Texas Education Agency / GCCISD accountability reporting
// - FBI UCR property/violent crime trend

export const baytownStats = {
  population: 86004,
  median_household_income: 59910,
  poverty_rate_pct: 17.7,
  under_18_pct: 27.7,
  seniors_65_plus_pct: 11.6,
  hispanic_latino_pct: 50.8,
  white_alone_pct: 45.8,
  black_alone_pct: 15.8,
  asian_alone_pct: 2.3,
  two_or_more_races_pct: 16.5,
  rising_housing_costs_pct: 77.6, // Of survey respondents over last 3 years
  housing_discrimination_perceived_pct: 47.4, // "Yes" + "Likely"
  income_under_50k_pct: 20.0, // Q15: $0-50k bands combined
};

export const demographics = [
  { group: "Hispanic / Latino", pct: 50.8, color: "#0A192F" },
  { group: "White (non-Hispanic alone)", pct: 45.8, color: "#334155" },
  { group: "Black / African American", pct: 15.8, color: "#8B0000" },
  { group: "Two or more races", pct: 16.5, color: "#D4AF37" },
  { group: "Asian", pct: 2.3, color: "#94A3B8" },
];

// Trend lines — illustrative approximations for the visual narrative
export const incomeTrend = [
  { year: "2015", median_income: 49800 },
  { year: "2017", median_income: 52400 },
  { year: "2019", median_income: 55300 },
  { year: "2021", median_income: 57100 },
  { year: "2023", median_income: 59910 },
];

export const povertyTrend = [
  { year: "2015", poverty: 17.1, child_poverty: 23.0 },
  { year: "2017", poverty: 17.6, child_poverty: 23.8 },
  { year: "2019", poverty: 17.9, child_poverty: 24.0 },
  { year: "2021", poverty: 18.0, child_poverty: 24.3 },
  { year: "2023", poverty: 17.7, child_poverty: 24.1 },
];

export const crimeTrend = [
  { year: "2018", property: 28.5, violent: 5.1 },
  { year: "2019", property: 27.2, violent: 4.9 },
  { year: "2020", property: 26.8, violent: 5.4 },
  { year: "2021", property: 25.4, violent: 5.2 },
  { year: "2022", property: 24.1, violent: 4.8 },
  { year: "2023", property: 23.0, violent: 4.6 },
];

export const educationScores = [
  { subject: "Reading", gccisd: 67, texas: 74 },
  { subject: "Math", gccisd: 62, texas: 70 },
  { subject: "Science", gccisd: 70, texas: 75 },
  { subject: "Graduation", gccisd: 88, texas: 90 },
];

// City of Baytown 2025 Consolidated Plan Survey results (n=98)
export const surveyPriorities = [
  { label: "Roads, sidewalks, and street lighting", pct: 72.6 },
  { label: "Rising housing costs (last 3 yrs)", pct: 77.6 },
  { label: "More affordable housing", pct: 49.5 },
  { label: "Better public safety", pct: 37.9 },
  { label: "Public transportation access", pct: 20.0 },
  { label: "Job training programs", pct: 13.7 },
];

// How Baytown Bridge MAPS to each of the City's 5 stated CDBG objectives
export const cdbgAlignment = [
  {
    id: 1,
    cityObjective: "Preserve & Expand Affordable Housing",
    cityNeed: "Rising housing costs reported by 77% of residents; aging stock; displacement risk for LMI families.",
    bridgeContribution:
      "Bridge connectors negotiate landlord relationships, coordinate with the Baytown Housing Authority on FSS/Section 8 utilization, and walk families from week-to-week rentals into stable leases or first-time homeownership readiness.",
  },
  {
    id: 2,
    cityObjective: "Improve Public Infrastructure in LMI Neighborhoods",
    cityNeed: "72% of residents cited sidewalks, lighting, and drainage as the top community priority.",
    bridgeContribution:
      "Connectors flag neighborhood-level infrastructure barriers (unsafe walking conditions for seniors, lighting gaps near after-school sites) directly back to Community Development — building a feedback loop the City currently lacks.",
  },
  {
    id: 3,
    cityObjective: "Support Public Services for Vulnerable Populations",
    cityNeed: "Senior nutrition, transportation, youth programs, and disability supports were identified as persistent service gaps.",
    bridgeContribution:
      "This is the heart of the Bridge model. One connector. One household. Active referrals across Harris Health, Meals on Wheels, Harris County Transit, GCCISD after-school, and disability services — for 12–24 months minimum.",
    primary: true,
  },
  {
    id: 4,
    cityObjective: "Prevent & Address Homelessness",
    cityNeed: "Growing eviction risk, no formal city shelter, heavy reliance on nonprofits.",
    bridgeContribution:
      "Bridge intake screens for housing instability on day one. Emergency rental/utility/LIHEAP coordination is built into the connector role — preventing eviction before the 5-day notice ever arrives.",
  },
  {
    id: 5,
    cityObjective: "Promote Economic Empowerment & Self-Sufficiency",
    cityNeed: "Underemployment, lack of job training, limited career-advancement pathways in LMI areas.",
    bridgeContribution:
      "Direct enrollment into Lee College certificates (medical billing, logistics, welding) paired with Workforce Solutions childcare subsidies — the City's existing investments, finally activated through a single relationship.",
  },
];

export const familyMetrics = [
  {
    domain: "Housing",
    low: "Facing eviction, unhoused, or paying greater than 50% of income on rent.",
    progressing: "Lease secured, but rent burden remains high or housing is temporary.",
    thriving: "Safe, affordable lease or homeownership; housing under 30% of income.",
  },
  {
    domain: "Employment",
    low: "Unemployed or underemployed in unstable, low-wage hourly gigs.",
    progressing: "Full-time employment without benefits or growth opportunities.",
    thriving: "Stable career with benefits, predictable income, and a clear growth path.",
  },
  {
    domain: "Education",
    low: "No high school diploma; no specialized skills or training.",
    progressing: "Has GED/HS diploma; enrolled or interested in Lee College training.",
    thriving: "Completed degree or high-demand certification; gainfully utilizing credentials.",
  },
  {
    domain: "Family Health",
    low: "Uninsured; frequent ER visits for routine care; missed child checkups.",
    progressing: "Insured via Medicaid/CHIP; has a PCP but inconsistent care.",
    thriving: "All family members have primary, preventative, and dental care.",
  },
];

export const seniorMetrics = [
  {
    domain: "Independent Living",
    low: "Cannot manage daily activities safely; home is inaccessible or unsafe.",
    progressing: "Manages daily activities with strain or via overstretched family care.",
    thriving: "Living safely at home with modifications and scheduled support services.",
  },
  {
    domain: "Healthcare Access",
    low: "Missing critical medications due to cost; skipping appointments without transport.",
    progressing: "Enrolled in Medicare/Medicaid; relies on inconsistent informal rides.",
    thriving: "Predictable transit to appointments; reliable mail-order Rx delivery.",
  },
  {
    domain: "Social Isolation",
    low: "No weekly contact; signs of severe withdrawal or depression.",
    progressing: "Occasional contact; sporadic senior center visits, no consistent circle.",
    thriving: "Integrated into weekly social, faith, or community programs; routine check-ins.",
  },
];

export const referralPartners = [
  { name: "Lee College", role: "High-demand certificates (medical billing, logistics, welding)", track: "family" },
  { name: "Workforce Solutions", role: "Childcare subsidies during training hours", track: "family" },
  { name: "Baytown Housing Authority", role: "Section 8 / FSS coordination & landlord network", track: "family" },
  { name: "Goose Creek CISD (GCCISD)", role: "Head Start enrollment & after-school programs", track: "family" },
  { name: "Harris Health System", role: "Primary care, chronic disease management, home health", track: "both" },
  { name: "Meals on Wheels", role: "Nutritional support for homebound seniors", track: "senior" },
  { name: "Harris County Transit", role: "Specialized medical & senior transit", track: "senior" },
  { name: "AARP / Senior Centers", role: "Tech training, scam protection, social connection", track: "senior" },
  { name: "Local CDBG Sub-Recipients", role: "Rental & utility emergency assistance (LIHEAP)", track: "both" },
];

export const gapAnalysis = [
  {
    title: "Family Self-Sufficiency (FSS) Program",
    operator: "Baytown Housing Authority",
    whatItDoes: "Voluntary case management for existing Section 8 / HCV holders to set financial goals.",
    whyItIsNotEnough: "Restricted to vouchers (waitlists are closed). Cannot serve the broader community of struggling single mothers or seniors who are not on federal housing assistance.",
  },
  {
    title: "Harris County Housing Stabilization",
    operator: "Harris County / 2-1-1",
    whatItDoes: "Eviction prevention, rental assistance, and coordinated homelessness intake.",
    whyItIsNotEnough: "Primarily a crisis-intervention model. Activated when an eviction notice is served — not a 12–24 month proactive plan to build long-term stability.",
  },
  {
    title: "CK Family Services / Behavioral Health",
    operator: "Medicaid-funded providers",
    whatItDoes: "Offers wraparound case management for families.",
    whyItIsNotEnough: "Restricted to families with a child's mental health diagnosis or active child welfare prevention — narrow eligibility, narrow scope.",
  },
];

export const chicagoHistoryPoints = [
  {
    period: "2001",
    title: "Joined The Woodlawn Organization / WCDC",
    body: "George Broughton began as a Case Manager Service Connector at The Woodlawn Organization / Woodlawn Community Development Corporation in Chicago — a federally-funded model linking public housing residents with employment, education, healthcare, and social services.",
  },
  {
    period: "2001–2007",
    title: "Six Years · 105-Family Caseload",
    body: "Over six years George managed a continuous caseload of 105 Chicago families, enhancing public housing quality and connecting households with the full spectrum of safety-net services. This is the operational depth Bridge brings to Baytown on day one.",
  },
  {
    period: "Outcomes",
    title: "Proven Multi-Year Stabilization",
    body: "Households moved from crisis-mode dependency to long-term housing security, sustained employment, and re-enrollment in education. The Service Connector framework has been repeatedly cited as a federally-recognized blueprint for high-touch case management.",
  },
  {
    period: "Today",
    title: "A Battle-Tested Blueprint for Baytown",
    body: "Baytown Bridge is a localized adaptation — same operational frame, sized for a mid-sized community with the dual-county landscape of Harris and Chambers.",
  },
];

// ---------------------- Founder Authority (from George Broughton III CV) ----------------------
export const founderTimeline = [
  {
    period: "Apr 2020 – Present",
    role: "Founder & CEO",
    org: "Sterile Mate LLC",
    location: "Houston, TX",
    body: "Founded Sterile Mate LLC in response to the COVID-19 pandemic, supplying back-ordered sterile-processing equipment, PPE, and hospital water filtration to the Texas Medical Center. Opened a North Houston distribution facility and built a national logistics footprint. Demonstrates the operational discipline required to launch a Baytown service-delivery agency from scratch.",
  },
  {
    period: "Jun 2015 – Sep 2021",
    role: "Flight Attendant Training & Development",
    org: "United Airlines",
    location: "CHI / LAX / IAH",
    body: "Designed and delivered new-hire and recurrent training curricula with strict FAA compliance, competency assessments, and service-protocol audits. The exact curriculum-design skill set Bridge needs to onboard and recertify service connectors.",
  },
  {
    period: "Jun 2007 – Sep 2021",
    role: "International Onboard Services Attendant",
    org: "United Airlines",
    location: "CHI / LAX / IAH",
    body: "14 years of high-pressure customer service across international routes. FAA-regulated safety, real-time problem solving, and cross-cultural communication at altitude.",
  },
  {
    period: "Apr 2001 – May 2007",
    role: "Case Manager · Service Connector",
    org: "The Woodlawn Organization / WCDC",
    location: "Chicago, IL",
    body: "Six years managing a 105-family caseload — the foundational experience the Baytown Bridge model is built on. Enhanced public housing quality and connected families to employment, education, healthcare, and social services.",
    highlight: true,
  },
  {
    period: "Sep 2000 – May 2004",
    role: "Marketing Director",
    org: "Concepts Unlimited Enterprise, Inc.",
    location: "Chicago, IL",
    body: "Led national sales & marketing for a startup urban clothing brand. Full P&L responsibility. Bridge brings that entrepreneurial DNA to the social-services side of the ledger.",
  },
  {
    period: "Jun 1999 – Jun 2000",
    role: "Project Consultant",
    org: "Celluland / Rogers AT&T",
    location: "Montreal, QC",
    body: "Built and trained a prepaid wireless sales team to top regional performance.",
  },
  {
    period: "May 1996 – May 1999",
    role: "Senior Account Executive",
    org: "Cellect Cellular / Celltalk Express",
    location: "Hoffman Estates, IL",
    body: "Managed retail partnerships and developed market strategies for wireless products across North America.",
  },
];

export const founderCompetencies = [
  "Service Connector Case Management (6 yrs · 105-family caseload)",
  "Training Curriculum Design (FAA-regulated, audit-tested)",
  "Startup Operations & P&L Leadership",
  "Healthcare Supply Chain & Logistics",
  "Business Development & Partnership Building",
  "Team Building, Recruitment & Development",
  "Market Analysis & Strategic Planning",
  "Negotiation & Executive Communication",
];

export const founderContact = {
  email: "george.broughton@sterilemate.com",
  phone: "323.243.9139",
  city: "Humble, TX 77396",
};

// ---------------------- Year 1 Budget · Tri-Portfolio Framework ----------------------
// The City of Baytown acts as Master Administrator ("The Hub") and awards
// three identical 5-year contracts (with annual review) to three nonprofit
// service providers ("The Spokes"). Each agency manages 500 households / yr.
// All positions receive a 3% annual raise. Non-salary operating costs
// (software, evaluation, admin, office ops) are held flat for conservatism.

export const operationalPhases = [
  {
    period: "Months 1–2",
    title: "City Council Authorization & Funding Allocation",
    body: "The Baytown City Council passes a resolution establishing the program. The Community Development Division pools capital — HUD Community Development Block Grants (CDBG), local Municipal Development District (MDD) funds, and Harris County matches.",
  },
  {
    period: "Months 3–4",
    title: "RFP Competitive Release & Agency Selection",
    body: "The City issues a formal Request for Proposal. Eligible local non-profits bid for one of three specialized portfolios. An evaluation committee selects three distinct agencies on organizational capacity and verifiable track record.",
  },
  {
    period: "Month 5",
    title: "Central Software & Protocol Standardization",
    body: "The City provisions a unified, secure data-tracking platform (Social Solutions Apricot or ClientTrack) for all three agencies. Assessment matrices, intake documentation, and performance benchmarks are locked in for uniform delivery.",
  },
  {
    period: "Month 6+",
    title: "Frontline Staffing & System Launch",
    body: "The three awarded agencies hire and onboard their full staffing complement — 1 LCSW Program Manager, 2 BS Social Work Track Supervisors, 13 Service Connectors, and 2 Intake & Data Specialists. Combined community marketing and direct data integration with the Baytown Housing Authority trigger intake of the first 1,500 active households.",
  },
];

export const centralHubLines = [
  {
    line: "Executive Program Director",
    detail: "1.0 FTE · Permanent City of Baytown employee · Top of the Hub org chart. Owns annual review of the three Spoke agencies, RFP renewals, and the agency-independence transition once the launch-phase Developer Group rolls off.",
    cost: 135000,
    salaried: true,
  },
  {
    line: "General Manager · Hub Operations",
    detail: "1.0 FTE · Day-to-day operations of the Central Coordination Unit. RFP compliance, vendor management, finance & budget oversight, board liaison.",
    cost: 105000,
    salaried: true,
  },
  {
    line: "Master Clinical Social Worker (LCSW-S)",
    detail: "1.0 FTE · Clinical Director with supervisory authority over the three agency LCSW Program Managers. Sets clinical protocols, signs off on intake matrices, enforces trauma-informed-care standards system-wide.",
    cost: 95000,
    salaried: true,
  },
  {
    line: "Central Field Manager · Quality & Compliance",
    detail: "1.0 FTE · Conducts unannounced spot agency visits, fidelity-to-model audits, on-site training delivery, and case-file sampling across all three Spokes.",
    cost: 72000,
    salaried: true,
  },
  {
    line: "System Auditor",
    detail: "1.0 FTE · Full-time City compliance manager monitoring performance metrics, financial reporting, and RFP deliverable scorecards.",
    cost: 68000,
    salaried: true,
  },
  {
    line: "Executive Assistant",
    detail: "1.0 FTE · Calendar, board coordination, partner-relations support, document control, Council briefing prep.",
    cost: 48000,
    salaried: true,
  },
  {
    line: "Payroll Taxes & Benefits Load",
    detail: "22% of City-employee salaries · scales annually with raises",
    cost: 115060,
    salaried: false,
    isFringe: true,
  },
  {
    line: "Data System Provisioning",
    detail: "Master software enterprise licensing — 45+ seats across 3 agencies (Apricot / ClientTrack)",
    cost: 32000,
    salaried: false,
  },
  {
    line: "Independent Annual Evaluation",
    detail: "Third-party academic or professional evaluation to verify community ROI",
    cost: 25000,
    salaried: false,
  },
  {
    line: "Indirect & Admin Cost",
    detail: "City legal, marketing, and general program administration overhead",
    cost: 15000,
    salaried: false,
  },
];

// Program Developer Group — Sterile Mate LLC consulting contract.
// 2-year firm engagement to (1) develop the curriculum, intake matrices, and
// data platform; (2) train-the-trainer for the Hub and the three Spokes.
// Plus 3 one-year option renewals at the City's discretion, designed to
// make Baytown's executive team independent of Sterile Mate after Year 2.
export const developerGroupTeam = [
  {
    role: "Principal Consultant & Program Creator",
    who: "George Broughton III · Sterile Mate LLC",
    detail: "Founding Architect of the model. Leads development, train-the-trainer, and 2-year handoff.",
    cost: 135000,
  },
  {
    role: "LCSW · Clinical Curriculum Lead",
    who: "Sub-contracted Houston-area LCSW",
    detail: "Trauma-informed care curriculum, clinical supervision protocols, intake matrix design.",
    cost: 95000,
  },
  {
    role: "Senior Living Social Worker",
    who: "Sub-contracted Texas-licensed senior-care SW",
    detail: "Senior Care Pipeline curriculum: aging-in-place, AAA navigation, in-home care coordination.",
    cost: 85000,
  },
  {
    role: "Employment Specialist",
    who: "Sub-contracted workforce-development expert",
    detail: "Trade Pipeline curriculum: Lee College certificate pathways, Workforce Solutions integration.",
    cost: 75000,
  },
  {
    role: "Continuing Education Specialist",
    who: "Sub-contracted adult-ed / training designer",
    detail: "Builds the connector recertification pipeline, manages the train-the-trainer materials library.",
    cost: 75000,
  },
];

export const developerGroupContract = [
  {
    period: "Year 1 (Firm)",
    phase: "Develop · Build · Train",
    cost: 515000,
    detail: "Months 1–6: program development (curriculum, matrices, data platform). Months 7–12: train-the-trainer rollout to the Hub executive team and all three Spoke agencies.",
    highlight: true,
  },
  {
    period: "Year 2 (Firm)",
    phase: "Stabilize · Audit · Hand off",
    cost: 257500,
    detail: "50% engagement. Quarterly fidelity audits, connector recertification, agency-supervisor coaching. Begins formal independence transition.",
  },
  {
    period: "Year 3 (Option)",
    phase: "Supervised Independence",
    cost: 125000,
    detail: "25% engagement at the City's option. Spot reviews, annual recertification refresh, problem-case consultation only.",
  },
  {
    period: "Year 4 (Option)",
    phase: "Advisory Only",
    cost: 75000,
    detail: "Quarterly advisory retainer at the City's option. Baytown executive team owns operations.",
  },
  {
    period: "Year 5 (Option)",
    phase: "Advisory Only",
    cost: 50000,
    detail: "Annual advisory check-in at the City's option. Full Baytown independence achieved.",
  },
];

export const developerGroupTotals = {
  year1: 515000,
  year2: 257500,
  twoYearFirm: 772500,
  fiveYearAllOptions: 1022500,
  teamSalariesYr1: 465000,
  directExpensesYr1: 50000,
};

export const triPortfolioROI = [
  {
    label: "Bridge · Recurring Cost / Household (Hub + Spokes)",
    value: "$2,756 / household / yr",
    note: "~$230 per month for ongoing case management — the steady-state cost once the Sterile Mate Developer Group rolls off.",
    highlight: true,
  },
  {
    label: "Skilled Nursing-Home Placement",
    value: "$90,000+ / yr",
    note: "Texas Medicaid average. Bridge prevents this with home modifications + Medicare home health.",
  },
  {
    label: "Emergency Shelter / Coordinated Entry",
    value: "~$35,000 / household",
    note: "Bridge intervenes 6 weeks earlier on average — before the 5-day eviction notice.",
  },
  {
    label: "Preventable Senior EMS Dispatch",
    value: "$1,800 each",
    note: "70% of senior ER trips are preventable with PCP continuity. Bridge installs that on day 30.",
  },
];

// 5-Year Forecast — Hub salaries 3% annual raise (6 city FTEs incl. EPD);
// per agency salaried base 3% raise; non-salary (software, evaluation, admin,
// office ops) held flat for conservatism. Developer Group is a separate fixed-
// price consulting line that declines as Baytown achieves independence.
// "system" = recurring (Hub + 3 Agencies). "systemAllIn" includes Dev Group.
export const fiveYearForecast = [
  { year: "Year 1", hub: 710060, perAgency: 1141500, totalAgencies: 3424500, devGroup: 515000, system: 4134560, systemAllIn: 4649560, costPerHH: 2756, costPerHHAllIn: 3100 },
  { year: "Year 2", hub: 729202, perAgency: 1174623, totalAgencies: 3523869, devGroup: 257500, system: 4253071, systemAllIn: 4510571, costPerHH: 2835, costPerHHAllIn: 3007 },
  { year: "Year 3", hub: 748918, perAgency: 1208740, totalAgencies: 3626220, devGroup: 125000, system: 4375138, systemAllIn: 4500138, costPerHH: 2917, costPerHHAllIn: 3000 },
  { year: "Year 4", hub: 769226, perAgency: 1243880, totalAgencies: 3731640, devGroup: 75000, system: 4500866, systemAllIn: 4575866, costPerHH: 3001, costPerHHAllIn: 3050 },
  { year: "Year 5", hub: 790143, perAgency: 1280074, totalAgencies: 3840222, devGroup: 50000, system: 4630365, systemAllIn: 4680365, costPerHH: 3087, costPerHHAllIn: 3120 },
];

export const fiveYearGrandTotal = 21894000; // Recurring (Hub + 3 Agencies only)
export const fiveYearAllInTotal = 22916500; // Recurring + Developer Group

export const systemTotals = {
  centralHub: 710060,
  perAgency: 1141500,
  agencyCount: 3,
  totalAgencies: 3424500,
  developerGroupYr1: 515000,
  grandTotal: 4134560, // Recurring Hub + 3 Agencies
  grandTotalAllIn: 4649560, // Plus Developer Group Yr 1
  householdsServed: 1500,
  costPerHousehold: 2756,
  costPerHouseholdAllIn: 3100,
  caseload: 38,
  programYears: 5,
  raisePct: 3,
  fiveYearTotal: 21894000,
  fiveYearTotalAllIn: 22916500,
};

export const agencyBudgetLines = [
  {
    role: "Program Manager (LCSW Required)",
    fte: "1.0",
    detail: "Licensed Clinical Social Worker · agency clinical lead, oversees both Family & Senior tracks · $75,000",
    cost: 75000,
    salaried: true,
  },
  {
    role: "Track Supervisors (BS Social Work)",
    fte: "2.0",
    detail: "1 Family-Track + 1 Senior-Track supervisor · BS in Social Work + experience required · $65,000 each",
    cost: 130000,
    salaried: true,
  },
  {
    role: "Service Connectors (Case Managers)",
    fte: "13.0",
    detail: "$48,000 each · case-management background preferred (not licensure-required) · 1:38 caseload cap",
    cost: 624000,
    salaried: true,
  },
  {
    role: "Intake & Data Specialists",
    fte: "2.0",
    detail: "$38,000 each · gatekeepers and compliance entry",
    cost: 76000,
    salaried: true,
  },
  {
    role: "Payroll Taxes & Benefits Load",
    fte: "—",
    detail: "Standard 22% of total agency salaries · scales annually with raises",
    cost: 199100,
    salaried: false,
    isFringe: true,
  },
  {
    role: "Localized Office Operations",
    fte: "—",
    detail: "Rent, field travel mileage, utilities, phone lines, supplies · held flat",
    cost: 37400,
    salaried: false,
  },
];

export const portfolioContracts = [
  {
    id: 1,
    name: "Agency 1 · Public Housing Portfolio",
    households: 500,
    cost: 1141500,
    focus:
      "Residents in Baytown Housing Authority properties. Embedded with on-site case management and the existing Family Self-Sufficiency (FSS) program.",
    color: "#0A192F",
  },
  {
    id: 2,
    name: "Agency 2 · Section 8 Voucher Portfolio",
    households: 500,
    cost: 1141500,
    focus:
      "Voucher-holders living in private-market units across Baytown. Landlord engagement, recertification support, and homeownership transition.",
    color: "#8B0000",
  },
  {
    id: 3,
    name: "Agency 3 · General Community Portfolio",
    households: 500,
    cost: 1141500,
    focus:
      "LMI households not on federal housing assistance — the cohort current programs CANNOT reach. Single mothers in private rentals, at-risk men, aging-in-place seniors.",
    color: "#D4AF37",
  },
];

// ---------------------- Implementation Strategy ----------------------
// 12-month execution arc inside Year 1, driven by the Sterile Mate Developer
// Group's firm "Develop · Build · Train" engagement. Five overlapping phases.

export const implementationStrategy = [
  {
    id: 1,
    period: "Months 1–6",
    title: "Program Development",
    lead: "Sterile Mate Developer Group",
    icon: "blueprint",
    body:
      "The Architect and the 4 sub-contracted specialists (Clinical LCSW, Senior Living SW, Employment, Continuing Ed) build the operating system: standardized intake matrix, Senior Care + Family + Trade Pipeline curricula, the data platform (Apricot or ClientTrack) configured for cross-Spoke reporting, and the KPI dashboards the City will use for annual agency review.",
    deliverables: [
      "Standardized intake matrix (Senior + Family + Multigenerational)",
      "Trauma-informed care clinical supervision protocols",
      "Senior Care Pipeline curriculum (aging-in-place, AAA, in-home care)",
      "Trade Pipeline curriculum (Lee College + Workforce Solutions)",
      "Apricot / ClientTrack platform deployed (45+ seats provisioned)",
      "Agency performance scorecard + KPI dashboards live in the platform",
    ],
    cost: 515000,
  },
  {
    id: 2,
    period: "Months 4–7",
    title: "Train-the-Trainer · Hub Executive Team",
    lead: "Sterile Mate Developer Group → City of Baytown",
    icon: "graduation",
    body:
      "Sterile Mate trains the City's 6-FTE Central Coordination Unit (EPD, GM, Master LCSW-S, Central Field Manager, System Auditor, Executive Assistant) to own clinical, compliance, and field-audit authority for the entire Tri-Portfolio system. A 12-week intensive at Sterile Mate's Houston facility plus on-site at City Hall, ending with each Hub member graduating with a written competency portfolio.",
    deliverables: [
      "Fidelity-to-model audit playbook (Central Field Manager)",
      "RFP compliance scorecard + remediation protocol (System Auditor)",
      "Clinical supervision cadence + crisis-escalation tree (Master LCSW-S)",
      "Vendor / partner SLA framework (General Manager)",
      "Council briefing & board package templates (Executive Assistant)",
      "Signed Hub Team Competency Portfolios (6 of 6)",
    ],
    cost: null,
    sourceFrom: "Year 1 Developer Group",
  },
  {
    id: 3,
    period: "Months 5–8",
    title: "Agency Onboarding & Training",
    lead: "Sterile Mate + Hub LCSW-S + Field Manager",
    icon: "users",
    body:
      "Once the RFP is awarded (~Month 4), Sterile Mate and the now-trained Hub jointly run training cohorts for the three Spoke agencies — 18 FTEs per agency (1 LCSW Program Manager + 2 BS-SW Track Supervisors + 13 Service Connectors + 2 Intake & Data Specialists). Eight-week curriculum with live shadowing on test intakes, a certification exam, and fidelity-to-model sign-off before any Spoke is cleared to accept its first household.",
    deliverables: [
      "Service Connector certification (54 of 54 across 3 agencies)",
      "LCSW Program Manager clinical supervision pact (3 of 3)",
      "Intake & Data Specialist platform certification (6 of 6)",
      "Track Supervisor (BS-SW) field shadow sign-off (6 of 6)",
      "Each agency's executive director countersigns fidelity-to-model agreement",
    ],
    cost: null,
    sourceFrom: "Year 1 Developer Group",
  },
  {
    id: 4,
    period: "Month 6",
    title: "Soft Launch · 150-Household Intake Pilot",
    lead: "All 3 Spokes + Hub + Sterile Mate (observer)",
    icon: "rocket",
    body:
      "Each Spoke takes 50 households (10% of its annual portfolio) through a 4-week controlled pilot to stress-test the platform, the intake protocol, and the handoff between Spokes and the partner network (Lee College, Harris Health, Housing Authority, AAA, Workforce Solutions). Weekly executive standup; daily platform feedback loop; documented punch-list before full launch is cleared.",
    deliverables: [
      "150 active households fully enrolled (50 per Spoke)",
      "Platform punch-list resolved (sub-72-hour ticket SLA)",
      "Partner-network handoff audit (all 9 referral partners)",
      "Pilot retrospective signed by Hub + 3 Spoke EDs",
      "Go / No-Go decision logged with City Council",
    ],
    cost: null,
    sourceFrom: "Year 1 Operating Budget",
  },
  {
    id: 5,
    period: "Months 7–12",
    title: "Full Launch · 1,500 Households",
    lead: "3 Spokes + Hub (Sterile Mate transitions to audit role)",
    icon: "broadcast",
    body:
      "Full intake of the remaining 1,350 households via the partner network — direct data integration with the Baytown Housing Authority for proactive recruitment, GCCISD family liaisons, Harris Health primary-care intake, AAA senior referrals, and a city-led public marketing campaign. The independent evaluator begins data collection. By Month 12, the program publishes its first annual public report and the City conducts its first formal annual review of all three Spokes.",
    deliverables: [
      "1,500 active households (500 per Spoke, full caseload)",
      "Caseload cap held at 1:38 across all 3 agencies",
      "Independent annual evaluation published",
      "Annual agency review (3 of 3) completed by City Council",
      "Year-2 contract renewals issued · Sterile Mate engagement steps down to 50%",
    ],
    cost: null,
    sourceFrom: "Year 1 Operating Budget",
  },
];

export const implementationGateChecks = [
  {
    gate: "Gate 1 · End of Month 6",
    name: "Soft-Launch Go / No-Go",
    criteria:
      "Platform live · 150-HH pilot complete · 95%+ certification pass rate · zero outstanding fidelity-to-model exceptions.",
  },
  {
    gate: "Gate 2 · End of Month 9",
    name: "Mid-Year Performance Review",
    criteria:
      "750-household milestone hit · caseload ≤ 1:38 across all 3 agencies · weekly KPI dashboard reviewed by Hub LCSW-S and System Auditor.",
  },
  {
    gate: "Gate 3 · End of Month 12",
    name: "Year-1 Annual Review · 3 Spokes",
    criteria:
      "1,500-HH milestone · independent evaluation published · agency-specific performance scorecards reviewed by City Council · Year-2 contract renewals authorized.",
  },
];


// Forward-looking targets for the Bridge model. These are the program-side
// commitments paired with the dollar-side commitments in fiveYearForecast.
// Ratios derived from the published research and Bridge's Chicago / WCDC
// operating history (105-family caseload × 6 years).
export const programGoalsForecast = [
  {
    year: "Year 1",
    households: 1500,
    seniors: 500,
    families: 1000,
    leeCollegeEnrollment: 240,
    leeCollegeGraduates: 0,
    homeMods: 180,
    medicareEnrollment: 425,
    benefitsRecovered: 2400000,
    evictionsPrevented: 145,
    note: "Year 1 launch: focus on intake, enrollment, and benefits navigation. First Lee College cohort enrolls; no graduates yet (12–24 mo certificates).",
  },
  {
    year: "Year 2",
    households: 1500,
    seniors: 500,
    families: 1000,
    leeCollegeEnrollment: 320,
    leeCollegeGraduates: 95,
    homeMods: 220,
    medicareEnrollment: 460,
    benefitsRecovered: 2750000,
    evictionsPrevented: 175,
    note: "First Lee College graduates enter the workforce. Senior home-mod backlog clears. Benefits recovery accelerates as enrollment matures.",
  },
  {
    year: "Year 3",
    households: 1500,
    seniors: 500,
    families: 1000,
    leeCollegeEnrollment: 360,
    leeCollegeGraduates: 165,
    homeMods: 200,
    medicareEnrollment: 475,
    benefitsRecovered: 2900000,
    evictionsPrevented: 190,
    note: "Steady-state. Workforce graduates earning $54k–$65k starting wages. Senior aging-in-place outcomes published in independent evaluation.",
  },
  {
    year: "Year 4",
    households: 1500,
    seniors: 500,
    families: 1000,
    leeCollegeEnrollment: 380,
    leeCollegeGraduates: 195,
    homeMods: 195,
    medicareEnrollment: 480,
    benefitsRecovered: 3000000,
    evictionsPrevented: 200,
    note: "First Bridge graduates qualify for first-time home purchase (Year 5+ ladder). Sterile Mate transitions to advisory only.",
  },
  {
    year: "Year 5",
    households: 1500,
    seniors: 500,
    families: 1000,
    leeCollegeEnrollment: 400,
    leeCollegeGraduates: 215,
    homeMods: 190,
    medicareEnrollment: 485,
    benefitsRecovered: 3100000,
    evictionsPrevented: 210,
    note: "Full Baytown independence. 5-year cohort of credentialed tradespeople, stabilized seniors, and first-generation homeowners is established.",
  },
];

export const programGoalsTotals = {
  householdYears: 7500, // 1500 HH × 5 years of service
  leeCollegeEnrollmentTotal: 1700,
  leeCollegeGraduatesTotal: 670,
  homeModsTotal: 985,
  medicareEnrollmentsTotal: 2325,
  benefitsRecoveredTotal: 14150000,
  evictionsPreventedTotal: 920,
};

// Program-wide outcome benchmarks (not year-bound — apply across the 5-year horizon).
export const programOutcomeBenchmarks = [
  {
    metric: "Caseload Cap (Service Connector : Households)",
    target: "1 : 38",
    rationale: "Federal high-touch case-management standard. WCDC Chicago operated at 1:35.",
  },
  {
    metric: "Intake → First Service",
    target: "Same day",
    rationale: "Single-intake model. Multi-generational household assessed and enrolled in one 90-minute visit.",
  },
  {
    metric: "Service Continuity",
    target: "12–24 months minimum",
    rationale: "Bridge is a stabilization program, not a crisis hotline. Long-tail relationship is the mechanism.",
  },
  {
    metric: "Annual Benefits Recovered / Senior",
    target: "$3,000–$6,000",
    rationale: "SNAP, LIHEAP, Texas Over-65 freeze, Medicare Savings, Extra Help, VA screening.",
  },
  {
    metric: "Preventable Senior EMS Reduction",
    target: "≥ 40% over 24 mo",
    rationale: "PCP continuity + chronic-disease management installed within 30 days of intake.",
  },
  {
    metric: "Lee College Completion Rate",
    target: "≥ 60% of enrolled cohort",
    rationale: "Childcare subsidy + connector retention support closes the dropout gap.",
  },
  {
    metric: "Independent Annual Evaluation",
    target: "Year-end published",
    rationale: "Third-party academic or professional evaluator. Public-record accountability.",
  },
  {
    metric: "Agency Performance Review",
    target: "Annual per Spoke",
    rationale: "RFP renewal contingent on benchmark performance. Non-performing agency replaced via re-bid.",
  },
];

export const factSheetNumbers = [
  { v: "86,004", k: "Baytown residents (Census 2024)" },
  { v: "$59,910", k: "Median household income" },
  { v: "17.7%", k: "Overall poverty rate" },
  { v: "27.7%", k: "Under age 18" },
  { v: "11.6%", k: "Seniors age 65+" },
  { v: "50.8%", k: "Hispanic or Latino" },
  { v: "77.6%", k: "Reporting rising housing costs (last 3 yrs)" },
  { v: "72.6%", k: "Citing sidewalks/lighting/roads as #1 priority" },
];

// ---------------------- Trade Pipeline ----------------------
// Verified job-market data for Baytown / Harris County trade pipeline
// Sources: ExxonMobil corporate (Baytown Complex), Houston Methodist
// Careers, Indeed Baytown HVAC search, Dallas Fed 2025 Southwest Economy
// report, Lee College Craft Employment data.

export const employerSignals = [
  {
    employer: "ExxonMobil Baytown Complex",
    headline: "8,500+",
    body:
      "Total Baytown workforce. Hires electricians, pipe fitters, iron workers, carpenters, helpers, and craft labor — the exact pipeline a young man without a 4-year degree can break into.",
    source: "ExxonMobil corporate, 2024",
  },
  {
    employer: "Houston Methodist Baytown Hospital",
    headline: "60+",
    body:
      "Active job postings on the campus, with a growing physician group and a deep bench of support-services and skilled-tech roles. New tower expansion in progress.",
    source: "Houston Methodist Careers, 2025",
  },
  {
    employer: "HVAC Demand · Baytown Area",
    headline: "~937",
    body:
      "Active HVAC contractor (411) + HVAC helper (526) openings indexed in the Baytown area. Lee College's HVAC starting wage is $54,371–$57,144/yr.",
    source: "Indeed + Lee College HVAC posting, 2025",
  },
  {
    employer: "Texas Construction & Health",
    headline: "#1 & #2",
    body:
      "The two industries driving 2025 job growth statewide are construction and health services — both with deep, active Baytown footprints (port projects, plant turnarounds, hospital expansion).",
    source: "Dallas Fed Southwest Economy, 2026 Q1",
  },
];

export const tradeTracks = [
  {
    name: "HVAC Technician",
    icon: "wind",
    startingPay: "$54,371 – $57,144 / yr",
    midPay: "$65k – $85k journeyman",
    timeToCredential: "12–18 months",
    program: "Lee College HVAC Technology Certificate",
    why: "Year-round Houston heat and humidity = recession-proof demand. Residential, commercial, and industrial pipelines never close.",
  },
  {
    name: "Welding / Pipefitting",
    icon: "flame",
    startingPay: "~$25.85 / hr (~$53k full-time)",
    midPay: "$70k – $100k specialized",
    timeToCredential: "9–15 months",
    program: "Lee College Welding Certificate",
    why: "Direct line into ExxonMobil, Chevron Phillips, and every refinery turnaround on the ship channel. 6G welders make six figures.",
  },
  {
    name: "Process Technology",
    icon: "settings",
    startingPay: "$60k – $75k / yr",
    midPay: "$85k – $110k operator",
    timeToCredential: "2-year AAS",
    program: "Lee College Process Technology AAS",
    why: "The Lee College program is built FOR the Baytown plants. Graduates are pre-recruited by ExxonMobil, Covestro, and Chevron Phillips.",
  },
  {
    name: "Commercial Driver (CDL)",
    icon: "truck",
    startingPay: "$50k – $65k / yr",
    midPay: "$70k – $95k tanker/hazmat",
    timeToCredential: "4–8 weeks",
    program: "Workforce Solutions CDL partner training",
    why: "Port of Houston is the #1 U.S. port by tonnage. Tanker and hazmat endorsements unlock the highest-paying loads.",
  },
  {
    name: "IT / Help Desk → Cybersecurity",
    icon: "cpu",
    startingPay: "$45k – $55k / yr",
    midPay: "$70k – $110k specialist",
    timeToCredential: "6–12 months (CompTIA / Google Cert)",
    program: "Lee College IT certificate + industry stackable creds",
    why: "Every Baytown employer needs help-desk and security staff. Remote-eligible ceiling; no physical wear on the body.",
  },
  {
    name: "Hospital Skilled Support",
    icon: "stethoscope",
    startingPay: "$42k – $55k / yr",
    midPay: "$65k – $90k surgical tech / sonography",
    timeToCredential: "6–24 months",
    program: "Lee College Allied Health certificates",
    why: "Houston Methodist Baytown is in active expansion. Medical billing, surgical tech, sonography, and respiratory therapy all hire locally.",
  },
];

export const salaryLadder = [
  {
    year: "Year 0",
    label: "Intake · At-Risk",
    income: "< $25,000",
    detail: "Underemployed gig work, unstable hours, no benefits, no path. This is the household Bridge meets.",
  },
  {
    year: "Year 1",
    label: "Apprentice / Helper",
    income: "$35k – $42k",
    detail: "Enrolled at Lee College or in CDL training. HVAC helper or pipe-fitter helper income while credentialing.",
  },
  {
    year: "Year 2",
    label: "Credentialed Tradesperson",
    income: "$54k – $65k",
    detail: "Certificate complete. HVAC tech, welder, CDL driver, or process operator. First time with benefits and PTO.",
  },
  {
    year: "Year 3–4",
    label: "Journeyman / Specialist",
    income: "$70k – $90k",
    detail: "6G welder, master HVAC, plant operator, or surgical tech. Mortgage-qualifying income with a stable W-2.",
  },
  {
    year: "Year 5+",
    label: "Homeowner",
    income: "$85k+",
    detail: "Down payment saved. Pre-qualified through Baytown Housing Authority and HUD first-time homebuyer programs. Generational wealth begins.",
    highlight: true,
  },
];

export const tradeVsCollege = {
  trade: {
    label: "Trade Certificate Path",
    rows: [
      "6 – 24 months to a credential",
      "$2,000 – $8,000 total tuition",
      "Starting at $45k–$60k year one",
      "Earning during training (helper / apprentice)",
      "Hands-on, locally hired, recession-resistant",
    ],
  },
  college: {
    label: "Traditional 4-Year Degree",
    rows: [
      "4 – 6 years to a credential",
      "$40,000 – $120,000 + debt",
      "Often $0 income during studies",
      "Average graduate debt: $30k+",
      "Field-mismatch risk; oversupplied entry roles",
    ],
  },
};

// ---------------------- Senior Care Pipeline ----------------------
// Verified senior-service partners for Harris County / Baytown.
// Sources: Houston-Harris County AAA (h-gac.com/area-agency-on-aging),
// Harris County RIDES (rides.harriscountytx.gov), Interfaith Ministries
// Meals on Wheels, YWCA Houston, Medicare.gov home-health coverage.

export const seniorPartnerSignals = [
  {
    partner: "Area Agency on Aging (AAA)",
    headline: "60+",
    body:
      "Houston-Harris County AAA is the federal hub for adults 60 and older. Benefits counseling, in-home supports, nutrition, caregiver help. Bridge connectors hold the warm handoff so seniors are not bounced through a hotline.",
    source: "h-gac.com/area-agency-on-aging · 1-800-437-7396",
  },
  {
    partner: "Harris County RIDES",
    headline: "200%",
    body:
      "Subsidized transit for seniors and people with disabilities at or below 200% of the Federal Poverty Level — the difference between a kept cardiologist appointment and a missed one.",
    source: "rides.harriscountytx.gov, 2025",
  },
  {
    partner: "Meals on Wheels · Interfaith Ministries",
    headline: "7-day",
    body:
      "Home-delivered meals seven days a week for homebound seniors. YWCA Houston operates congregate meals at neighborhood senior centers. Bridge enrolls clients on intake day, not after a hospitalization.",
    source: "Interfaith Ministries / YWCA Houston, 2025",
  },
  {
    partner: "Harris Health · Medicare Home Health",
    headline: "Skilled",
    body:
      "Part-time skilled nursing, PT, OT, and home health aide visits for homebound seniors — fully Medicare-covered when properly enrolled. The connector handles the paperwork so the senior never falls through enrollment gaps.",
    source: "Medicare.gov + Harris Health System, 2025",
  },
];

export const seniorPillars = [
  {
    name: "Housing & Home Modification",
    icon: "home",
    headline: "Aging-in-place, safely.",
    body:
      "Grab bars, shower benches, threshold ramps, lever handles, motion lighting, smoke + CO detectors. Falls are the #1 reason a senior loses their home — Bridge prevents the fall before it happens. Coordinated with the Baytown Housing Authority's home repair set-aside and CDBG single-family rehab dollars.",
  },
  {
    name: "In-Home Care",
    icon: "heart-pulse",
    headline: "Help under the same roof.",
    body:
      "Medicare home-health navigation (skilled nursing, PT/OT, aide visits), personal care attendants through AAA in-home services, and respite for overstretched family caregivers. Bridge writes the care plan, schedules the visits, and follows up.",
  },
  {
    name: "Health Navigation",
    icon: "stethoscope",
    headline: "One doctor. One plan. One number to call.",
    body:
      "Medicare + Medicaid dual-eligible enrollment, primary care assignment through Harris Health, chronic disease management (diabetes, hypertension, COPD), Rx affordability programs, and telehealth setup. Every senior leaves intake with a Primary Care relationship.",
  },
  {
    name: "Benefits & Resources",
    icon: "wallet",
    headline: "Every dollar the senior is owed.",
    body:
      "SNAP food benefits, LIHEAP utility assistance, property tax exemption (Texas Over-65 freeze), Medicare Savings Program, Extra Help with Rx costs, VA benefits screening. The average enrolled senior recovers $3,000–$6,000 a year in benefits already on the books.",
  },
  {
    name: "Social Connection & Programs",
    icon: "users",
    headline: "Isolation is a clinical risk factor.",
    body:
      "Weekly senior-center attendance, congregate meals, faith community pairings, tech training to close the digital divide, scam-protection education, and a Senior Companion volunteer phone-check on every Bridge senior. Isolation is treated like the disease it is.",
  },
  {
    name: "Encore Employment & Purpose",
    icon: "briefcase",
    headline: "Work, on the senior's terms.",
    body:
      "Senior Community Service Employment Program (SCSEP), AAA part-time placements, RSVP volunteer matches, and remote help-desk pathways for seniors who want or need income. Purpose is preventative medicine — and a part-time wage stabilizes fixed-income budgets.",
  },
];

export const seniorLadder = [
  {
    step: "Day 0",
    label: "Crisis · Intake",
    state: "Unsafe Home",
    detail:
      "Missed medications. No transport. Isolated. Stairs they can't climb. This is the senior Bridge meets.",
  },
  {
    step: "30 Days",
    label: "Stabilized",
    state: "Benefits + PCP",
    detail:
      "Medicare/Medicaid enrolled, SNAP and LIHEAP applied, primary care reconnected, home safety assessment scheduled.",
  },
  {
    step: "90 Days",
    label: "Resourced",
    state: "Home Mods + Rides",
    detail:
      "Grab bars and ramps installed. Harris County RIDES scheduled for monthly appointments. In-home care visits underway.",
  },
  {
    step: "6–12 Months",
    label: "Connected",
    state: "Weekly Touch-Points",
    detail:
      "Mail-order Rx delivery. Weekly senior center or faith-community meals. Senior Companion phone check-ins. Caregiver respite for the family.",
  },
  {
    step: "Ongoing",
    label: "Aging in Place",
    state: "Safe · Social · Dignified",
    detail:
      "Living safely at home. Integrated socially. Financially protected. Generational dignity preserved.",
    highlight: true,
  },
];

export const seniorImpactStats = [
  { k: "$3,000–$6,000", v: "Average annual benefits recovered per enrolled senior" },
  { k: "70%", v: "Of senior emergency-room visits are preventable with primary-care continuity" },
  { k: "#1", v: "Cause of nursing-home placement nationally: a single fall at home" },
  { k: "12.6%", v: "Baytown residents are 65+ — and growing every year" },
];

// ---------------------- Multigenerational Story ----------------------
// One household. One intake. One day. Three generations.

export const multigenHousehold = {
  family: "The Hernandez Household · Baytown, TX",
  setup:
    "One bungalow on a tree-lined Baytown street. Three generations sharing three bedrooms. One Bridge connector at the kitchen table for an intake that lasts 90 minutes — and changes the trajectory of all three lives.",
  members: [
    {
      role: "Mother",
      name: "Maria, 34",
      situation:
        "Working split shifts at a gas station for $12/hr with no benefits. Wants to enroll at Lee College for medical billing but cannot afford childcare or the lost income.",
      activations: [
        "Lee College medical billing certificate enrollment",
        "Workforce Solutions childcare subsidy approved",
        "Texas Rent Relief application filed (back rent stabilized)",
        "Medicaid + Marketplace ACA assignment",
      ],
      accent: "#0A192F",
      icon: "user-round",
    },
    {
      role: "Child",
      name: "Diego, 7",
      situation:
        "Behind on early reading after two pandemic-affected school years. After-school hours unsupervised because Maria works until 9 p.m.",
      activations: [
        "GCCISD Head Start / after-school placement",
        "Pediatric primary care via Harris Health",
        "Free dental + vision through CHIP",
        "Weekend literacy tutor through Lee College practicum",
      ],
      accent: "#8B0000",
      icon: "baby",
    },
    {
      role: "Grandmother",
      name: "Rosa, 68",
      situation:
        "Helps Maria with Diego when she can, but a recent hip fracture has made stairs dangerous. Skipping her blood pressure medication due to cost.",
      activations: [
        "Medicare home health PT + aide visits",
        "Home modification: grab bars, ramps, lever handles",
        "SNAP + LIHEAP + Texas Over-65 tax freeze filed",
        "Harris County RIDES scheduled for monthly cardiology",
      ],
      accent: "#D4AF37",
      icon: "user-2",
    },
  ],
};

export const multigenROI = {
  siloed: {
    label: "Traditional Siloed Approach",
    rows: [
      { metric: "Case workers involved", value: "3 separate agencies" },
      { metric: "Intake appointments", value: "5–7 over 6 weeks" },
      { metric: "Forms filled across systems", value: "20+" },
      { metric: "Days from first call to first service", value: "30–90" },
      { metric: "Coordination across silos", value: "Falls on the mother" },
    ],
  },
  bridge: {
    label: "Bridge — Single Intake",
    rows: [
      { metric: "Case workers involved", value: "1 connector" },
      { metric: "Intake appointments", value: "1 (90 minutes)" },
      { metric: "Forms filled across systems", value: "Connector handles them" },
      { metric: "Days from first call to first service", value: "Same day" },
      { metric: "Coordination across silos", value: "Owned by the connector" },
    ],
  },
};

